/**
 * Tela /campaigns/:campaignId — editor de campanha (TH-escopo-futuro).
 *
 * Referência visual: design-system/prototype/painel-screens.jsx — PainelEditor.
 *
 * Layout: 2 colunas — coluna principal (preview TV 16:9 + lista de SKUs)
 * e coluna lateral (inspector com campos + card de sync).
 *
 * Mock data apenas. Backend de campanhas chega pós-MVP (fora dos marcos M0–M6);
 * ver CLAUDE.md: "Provavelmente nova entidade `campaigns` em fase pós-MVP".
 *
 * TODO: substituir mock por hook useCampaign(id) + TanStack Query quando
 *       endpoint GET /api/campaigns/:id for implementado.
 * TODO: botões "Pré-visualizar" e "Publicar" são stubs — conectar às
 *       ações reais quando backend de campanhas chegar.
 */

import { useParams } from "@tanstack/react-router";
import { Plus } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SkuRow {
  code: string;
  name: string;
  regular: string;
  oferta: string;
}

interface InspectorField {
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Mock data — substituir por API quando backend de campanhas chegar (pós-MVP)
// ---------------------------------------------------------------------------

const MOCK_SKUS: SkuRow[] = [
  {
    code: "7891000051023",
    name: "Arroz Tio João Tipo 1 5kg",
    regular: "32,90",
    oferta: "24,49",
  },
  {
    code: "7891234567890",
    name: "Feijão Camil Carioca 1kg",
    regular: "8,99",
    oferta: "7,49",
  },
  {
    code: "7891111222233",
    name: "Óleo Soya Soja 900ml",
    regular: "6,99",
    oferta: "5,99",
  },
  {
    code: "7891999000111",
    name: "Leite Italac Integral 1L",
    regular: "5,49",
    oferta: "4,99",
  },
];

const MOCK_INSPECTOR_FIELDS: InspectorField[] = [
  { label: "Layout", value: "4 produtos · grade 2×2" },
  { label: "Telas alvo", value: "Todas as 22 telas" },
  { label: "Início", value: "Sex 25/04 · 18:00" },
  { label: "Fim", value: "Dom 27/04 · 22:00" },
  { label: "Rotação", value: "12 segundos por slide" },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Preview da arte no formato TV 16:9 com mini-grade de SKUs. */
function TVPreview({ skus }: { skus: SkuRow[] }) {
  return (
    <div>
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
        Pré-visualização · Layout 4 produtos
      </div>
      <div className="aspect-video overflow-hidden rounded-lg border border-[var(--color-border-strong)] bg-black p-3">
        <div className="flex h-full w-full flex-col gap-2 rounded bg-[var(--color-background)] p-3 text-white">
          {/* Header mini */}
          <div className="flex justify-between font-mono text-[9px] text-white/50">
            <span>VILA NOVA · TV-001</span>
            <span>OFERTA · FIM DE SEMANA</span>
          </div>
          {/* Grade 2×2 */}
          <div className="grid flex-1 grid-cols-2 grid-rows-2 gap-1.5">
            {skus.map((s) => (
              <div
                key={s.code}
                className="flex min-w-0 flex-col justify-between rounded border border-white/[0.08] bg-white/[0.04] p-2"
              >
                <div className="truncate text-[8px] text-white/70">
                  {s.name}
                </div>
                <div className="font-mono text-[28px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-[var(--color-primary)]">
                  <small className="text-[10px] opacity-60">R$</small>
                  {s.oferta}
                </div>
              </div>
            ))}
          </div>
          {/* Footer mini */}
          <div className="flex justify-between font-mono text-[8px] text-white/40">
            <span>Válido até 28/04 · enquanto durar estoque</span>
            <span>14:32</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Lista de SKUs em rotação. */
function SkuList({ skus }: { skus: SkuRow[] }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
          SKUs em rotação · {skus.length} de 24
        </div>
        {/* TODO: conectar ao fluxo de adição de SKU quando backend chegar (pós-MVP) */}
        <Button variant="outline" size="sm" disabled>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Adicionar SKU
        </Button>
      </div>
      <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
        {skus.map((s, i) => (
          <div
            key={s.code}
            className={`grid items-center gap-3 px-3.5 py-2.5 text-sm ${
              i !== 0 ? "border-t border-[var(--color-border)]" : ""
            }`}
            style={{ gridTemplateColumns: "20px 1fr 100px 100px 20px" }}
          >
            <span className="font-mono text-[10px] text-[var(--color-muted-foreground)]">
              ⋮⋮
            </span>
            <div>
              <div className="font-medium">{s.name}</div>
              <div className="font-mono text-[10px] text-[var(--color-muted-foreground)]">
                {s.code}
              </div>
            </div>
            <div className="text-right font-mono text-xs text-[var(--color-muted-foreground)] line-through">
              R$ {s.regular}
            </div>
            <div className="text-right font-mono text-sm font-semibold text-[var(--color-primary)]">
              R$ {s.oferta}
            </div>
            <span className="text-[var(--color-muted-foreground)]">···</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Painel lateral com campos da campanha + card de sync. */
function Inspector({ fields }: { fields: InspectorField[] }) {
  return (
    <aside className="flex flex-col gap-5 overflow-y-auto border-l border-[var(--color-border)] bg-[var(--color-card)] p-5">
      {fields.map((f) => (
        <div key={f.label}>
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
            {f.label}
          </div>
          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-2 text-xs text-[var(--color-foreground)]">
            {f.value}
          </div>
        </div>
      ))}

      {/* Card de sync estimada */}
      <div className="rounded-md border border-[var(--color-primary-soft-border)] bg-[var(--color-primary-soft)] p-3">
        <div className="mb-1 text-xs font-semibold text-[var(--color-primary)]">
          ⚡ Sync estimada
        </div>
        <div className="text-xs leading-relaxed text-[var(--color-muted-foreground)]">
          ~32 segundos para cair em todas as 22 telas após publicação.
        </div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CampaignEditorPage() {
  const { campaignId } = useParams({ from: "/campaigns/$campaignId" });

  const isNew = campaignId === "new";
  const campaignName = isNew ? "Nova campanha" : "Promo Final de Semana";
  const breadcrumb = isNew ? "CAMPANHAS / NOVA" : "CAMPANHAS / EDITOR";

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb={breadcrumb}
        title={campaignName}
        actions={
          <>
            {/* Rascunho usa tone "warn" conforme PainelEditor:279 */}
            <StatusPill tone="warn">Rascunho</StatusPill>
            {/* TODO: implementar pré-visualização quando backend de campanhas chegar (pós-MVP) */}
            <Button variant="outline" size="sm" disabled>
              Pré-visualizar
            </Button>
            {/* TODO: implementar publicação quando backend de campanhas chegar (pós-MVP) */}
            <Button size="sm" disabled>
              Publicar
            </Button>
          </>
        }
      >
        {/*
         * Override do padding padrão do AppLayout para o layout de 2 colunas
         * ocupar toda a altura disponível — necessário para o inspector lateral
         * ficar colado na borda.
         */}
        <div className="-m-6 flex h-[calc(100vh-var(--topbar-height,60px))] overflow-hidden">
          {/* Coluna principal */}
          <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-7">
            <TVPreview skus={MOCK_SKUS} />
            <SkuList skus={MOCK_SKUS} />
          </div>

          {/* Coluna lateral — inspector */}
          <div className="w-80 shrink-0">
            <Inspector fields={MOCK_INSPECTOR_FIELDS} />
          </div>
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
